import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '@/styles/quill-custom.css';
import '@/styles/resizable-video.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Youtube, Upload, Lightbulb } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { InteractiveElementSelector } from './InteractiveElementSelector';

export interface RichTextEditorRef {
  getContent: () => string;
  setContent: (content: string) => void;
  insertYouTubeVideo: (url: string) => void;
  insertUploadedFile: (fileUrl: string, fileName: string, fileType: string) => void;
}

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  moduleId?: string;
}

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ]
};

const formats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align',
  'blockquote', 'code-block', 'link', 'image', 'video'
];

export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  ({ value = '', onChange, placeholder = 'Start typing your content...', moduleId }, ref) => {
    const [content, setContent] = useState(value);
    const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
    const [youtubeUrl, setYoutubeUrl] = useState('');
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [showInteractiveSelector, setShowInteractiveSelector] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [quillRef, setQuillRef] = useState<ReactQuill | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

    // Update content when value prop changes
    useEffect(() => {
      console.log('RichTextEditor: value prop changed', { value, content, valueLength: value?.length });
      if (value !== content) {
        console.log('RichTextEditor: updating content from value prop');
        setContent(value);
      }
    }, [value, content]);

    useImperativeHandle(ref, () => ({
      getContent: () => content,
      setContent: (newContent: string) => {
        setContent(newContent);
        onChange?.(newContent);
      },
      insertYouTubeVideo: (url: string) => {
        const videoId = extractYouTubeId(url);
        if (videoId) {
          const embedHtml = `<div class="video-container" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; background: #000;">
            <iframe src="https://www.youtube.com/embed/${videoId}" 
                    frameborder="0" 
                    allowfullscreen 
                    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe>
          </div><p><br></p>`;
          const newContent = content + embedHtml;
          setContent(newContent);
          onChange?.(newContent);
        }
      },
      insertUploadedFile: (fileUrl: string, fileName: string, fileType: string) => {
        let fileHtml = '';
        if (fileType.startsWith('image/')) {
          fileHtml = `<img src="${fileUrl}" alt="${fileName}" style="max-width: 100%; height: auto;" /><p><br></p>`;
        } else if (fileType.startsWith('video/')) {
          fileHtml = `<video controls style="max-width: 100%; height: auto;">
            <source src="${fileUrl}" type="${fileType}" />
            Your browser does not support the video tag.
          </video><p><br></p>`;
        } else {
          fileHtml = `<p><a href="${fileUrl}" target="_blank" rel="noopener noreferrer">📄 ${fileName}</a></p><p><br></p>`;
        }
        const newContent = content + fileHtml;
        setContent(newContent);
        onChange?.(newContent);
      }
    }));

    const handleContentChange = (newContent: string) => {
      setContent(newContent);
      onChange?.(newContent);
    };

    const extractYouTubeId = (url: string): string | null => {
      const regExp = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
      const match = url.match(regExp);
      return (match && match[1].length === 11) ? match[1] : null;
    };

    const insertVimeoVideo = (url: string) => {
      const extractVimeoId = (url: string): string | null => {
        const regExp = /(?:vimeo\.com\/(?:.*\/)?)([\d]+)/;
        const match = url.match(regExp);
        return match ? match[1] : null;
      };

      const videoId = extractVimeoId(url);
      if (!videoId) {
        return false;
      }

      const embedHtml = `<div class="video-container resizable-video draggable-video" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: visible; max-width: 100%; background: #000; border: 2px solid transparent; cursor: move; resize: both; min-width: 300px; min-height: 169px; margin: 10px 0;" data-video-type="vimeo" data-video-id="${videoId}" draggable="true">
        <iframe src="https://player.vimeo.com/video/${videoId}?byline=0&portrait=0" 
                frameborder="0" 
                allow="autoplay; fullscreen; picture-in-picture" 
                allowfullscreen
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; border-radius: 4px;"></iframe>
        <div class="resize-handle" style="position: absolute; bottom: 0; right: 0; width: 20px; height: 20px; background: rgba(59, 130, 246, 0.9); cursor: se-resize; border-left: 3px solid #fff; border-top: 3px solid #fff; z-index: 1000; border-bottom-right-radius: 4px;"></div>
        <div class="drag-handle" style="position: absolute; top: 5px; left: 5px; width: 28px; height: 28px; background: rgba(59, 130, 246, 0.9); cursor: move; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; color: white; z-index: 1000; border: 2px solid white;">⋮⋮</div>
      </div><p><br></p>`;
      
      const newContent = content + embedHtml;
      setContent(newContent);
      onChange?.(newContent);
      return true;
    };

    const insertYouTubeVideo = (url: string) => {
      const videoId = extractYouTubeId(url);
      if (!videoId) {
        // Try Vimeo if YouTube fails
        return insertVimeoVideo(url);
      }

      const embedHtml = `<div class="video-container resizable-video draggable-video" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: visible; max-width: 100%; background: #000; border: 2px solid transparent; cursor: move; resize: both; min-width: 300px; min-height: 169px; margin: 10px 0;" data-video-type="youtube" data-video-id="${videoId}" draggable="true">
        <iframe src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen 
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; border-radius: 4px;"></iframe>
        <div class="resize-handle" style="position: absolute; bottom: 0; right: 0; width: 20px; height: 20px; background: rgba(59, 130, 246, 0.9); cursor: se-resize; border-left: 3px solid #fff; border-top: 3px solid #fff; z-index: 1000; border-bottom-right-radius: 4px;"></div>
        <div class="drag-handle" style="position: absolute; top: 5px; left: 5px; width: 28px; height: 28px; background: rgba(59, 130, 246, 0.9); cursor: move; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; color: white; z-index: 1000; border: 2px solid white;">⋮⋮</div>
      </div><p><br></p>`;
      
      const newContent = content + embedHtml;
      setContent(newContent);
      onChange?.(newContent);
      return true;
    };

    const handleInsertYouTube = () => {
      const success = insertYouTubeVideo(youtubeUrl);
      if (!success) {
        toast({
          title: "Invalid Video URL",
          description: "Please enter a valid YouTube or Vimeo URL",
          variant: "destructive"
        });
        return;
      }

      setYoutubeUrl('');
      setShowYouTubeDialog(false);

      toast({
        title: "Video Added",
        description: "Video has been embedded in your content"
      });
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!user) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to upload files",
          variant: "destructive"
        });
        return;
      }

      setUploading(true);

      try {
        // Use user ID in the file path to match RLS policies
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('course-materials')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('course-materials')
          .getPublicUrl(fileName);

        // Insert the file into content based on type
        let fileHtml = '';
        if (file.type.startsWith('image/')) {
          fileHtml = `<img src="${data.publicUrl}" alt="${file.name}" style="max-width: 100%; height: auto;" /><p><br></p>`;
        } else if (file.type.startsWith('video/')) {
          fileHtml = `<div class="video-container" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; background: #000; border-radius: 8px;">
            <video controls style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 8px;" preload="metadata">
              <source src="${data.publicUrl}" type="${file.type}" />
              Your browser does not support the video tag.
            </video>
          </div><p><br></p>`;
        } else if (file.type.startsWith('audio/')) {
          fileHtml = `<audio controls style="max-width: 100%;">
            <source src="${data.publicUrl}" type="${file.type}" />
            Your browser does not support the audio tag.
          </audio><p><br></p>`;
        } else if (file.name.toLowerCase().endsWith('.zip') && (file.name.toLowerCase().includes('scorm') || file.name.toLowerCase().includes('course'))) {
          // Handle SCORM packages - initiate processing
          const tempId = Date.now().toString();
          fileHtml = `<div class="scorm-package" data-temp-id="${tempId}" style="border: 2px dashed #ccc; padding: 20px; margin: 10px 0; text-align: center; background: #f9f9f9;">
            <h4>📦 SCORM Package: ${file.name}</h4>
            <p>Interactive SCORM-compliant learning content</p>
            <div class="scorm-processing" data-url="${data.publicUrl}" data-filename="${file.name}">
              <p style="color: #666;">✅ Uploaded successfully - Ready for course creation</p>
              <p style="font-size: 12px; color: #888;">SCORM content will be processed when the course is saved</p>
            </div>
          </div><p><br></p>`;
        } else {
          fileHtml = `<p><a href="${data.publicUrl}" target="_blank" rel="noopener noreferrer">📄 ${file.name}</a></p><p><br></p>`;
        }

        const newContent = content + fileHtml;
        setContent(newContent);
        onChange?.(newContent);
        setShowFileDialog(false);

        toast({
          title: "File Uploaded",
          description: `${file.name} has been added to your content`
        });

      } catch (error) {
        console.error('Error uploading file:', error);
        toast({
          title: "Upload Error",
          description: "Failed to upload file. Please try again.",
          variant: "destructive"
        });
      } finally {
        setUploading(false);
        // Reset input
        event.target.value = '';
      }
    };

    const setupVideoInteractivity = () => {
      console.log('Setting up video interactivity...');
      // Add event listeners for video dragging and resizing
      const videos = document.querySelectorAll('.resizable-video');
      console.log('Found videos:', videos.length);
      
      videos.forEach((video, index) => {
        const element = video as HTMLElement;
        console.log(`Setting up video ${index + 1}`);
        
        // Remove existing event listeners to prevent duplicates
        element.removeAttribute('data-listeners-attached');
        
        // Skip if already processed
        if (element.getAttribute('data-listeners-attached') === 'true') {
          console.log(`Video ${index + 1} already has listeners`);
          return;
        }
        
        // Make videos draggable
        element.draggable = true;
        
        element.addEventListener('dragstart', (e) => {
          console.log('Drag start');
          e.dataTransfer?.setData('text/html', element.outerHTML);
          element.style.opacity = '0.5';
        });

        element.addEventListener('dragend', (e) => {
          console.log('Drag end');
          element.style.opacity = '1';
        });

        // Handle resize
        const resizeHandle = element.querySelector('.resize-handle') as HTMLElement;
        if (resizeHandle) {
          console.log(`Adding resize listener to video ${index + 1}`);
          let isResizing = false;
          let startX: number, startY: number, startWidth: number, startHeight: number;

          resizeHandle.addEventListener('mousedown', (e) => {
            console.log('Resize mousedown');
            e.preventDefault();
            e.stopPropagation();
            isResizing = true;
            startX = e.clientX;
            startY = e.clientY;
            startWidth = parseInt(window.getComputedStyle(element).width, 10);
            startHeight = parseInt(window.getComputedStyle(element).height, 10);
            
            element.classList.add('editing');
            
            const mouseMoveHandler = (e: MouseEvent) => {
              if (!isResizing) return;
              const width = startWidth + e.clientX - startX;
              const height = startHeight + e.clientY - startY;
              
              if (width > 300 && height > 169) {
                element.style.width = width + 'px';
                element.style.height = height + 'px';
                element.style.paddingBottom = '0';
              }
            };

            const mouseUpHandler = () => {
              console.log('Resize mouseup');
              isResizing = false;
              element.classList.remove('editing');
              document.removeEventListener('mousemove', mouseMoveHandler);
              document.removeEventListener('mouseup', mouseUpHandler);
            };

            document.addEventListener('mousemove', mouseMoveHandler);
            document.addEventListener('mouseup', mouseUpHandler);
          });
        } else {
          console.log(`No resize handle found for video ${index + 1}`);
        }

        // Handle drag handle
        const dragHandle = element.querySelector('.drag-handle') as HTMLElement;
        if (dragHandle) {
          console.log(`Adding drag listener to video ${index + 1}`);
          dragHandle.addEventListener('mousedown', (e) => {
            console.log('Drag handle mousedown');
            e.preventDefault();
            let isDragging = false;
            let startX = e.clientX;
            let startY = e.clientY;
            let startLeft = element.offsetLeft;
            let startTop = element.offsetTop;

            element.style.position = 'relative';
            element.style.zIndex = '1000';

            const mouseMoveHandler = (e: MouseEvent) => {
              if (!isDragging) {
                isDragging = true;
                element.classList.add('editing');
              }
              
              const deltaX = e.clientX - startX;
              const deltaY = e.clientY - startY;
              
              element.style.left = (startLeft + deltaX) + 'px';
              element.style.top = (startTop + deltaY) + 'px';
            };

            const mouseUpHandler = () => {
              console.log('Drag handle mouseup');
              isDragging = false;
              element.classList.remove('editing');
              element.style.zIndex = '';
              document.removeEventListener('mousemove', mouseMoveHandler);
              document.removeEventListener('mouseup', mouseUpHandler);
            };

            document.addEventListener('mousemove', mouseMoveHandler);
            document.addEventListener('mouseup', mouseUpHandler);
          });
        } else {
          console.log(`No drag handle found for video ${index + 1}`);
        }
        
        // Mark as processed
        element.setAttribute('data-listeners-attached', 'true');
        console.log(`Video ${index + 1} setup complete`);
      });
    };

    // Use MutationObserver to detect when content changes and re-setup video interactions
    const setupMutationObserver = () => {
      const editorElement = document.querySelector('.ql-editor');
      if (!editorElement) return;

      const observer = new MutationObserver((mutations) => {
        let hasVideoChanges = false;
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.classList?.contains('resizable-video') || 
                    element.querySelector?.('.resizable-video')) {
                  hasVideoChanges = true;
                }
              }
            });
          }
        });
        
        if (hasVideoChanges) {
          console.log('Video content detected, re-setting up interactivity');
          setTimeout(() => setupVideoInteractivity(), 100);
        }
      });

      observer.observe(editorElement, {
        childList: true,
        subtree: true
      });

      return observer;
    };

    // Set up video functionality and mutation observer
    useEffect(() => {
      const timer = setTimeout(() => {
        setupVideoInteractivity();
        const observer = setupMutationObserver();
        
        return () => {
          observer?.disconnect();
        };
      }, 100);
      
      return () => clearTimeout(timer);
    }, [content]);

    return (
      <div className="space-y-4 w-full">
        {/* Toolbar */}
        <div className="flex gap-2 border-b pb-2">
          <Dialog open={showYouTubeDialog} onOpenChange={setShowYouTubeDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Youtube className="h-4 w-4 mr-2" />
                YouTube
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-background">
              <DialogHeader>
                <DialogTitle>Add Video</DialogTitle>
                <DialogDescription>
                  Add a YouTube or Vimeo video to your course content
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Video URL</Label>
                  <Input
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="Enter YouTube or Vimeo URL (e.g., https://www.youtube.com/watch?v=... or https://vimeo.com/...)"
                  />
                </div>
                <Button onClick={handleInsertYouTube} className="w-full">
                  Insert Video
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showFileDialog} onOpenChange={setShowFileDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-background">
              <DialogHeader>
                <DialogTitle>Upload File</DialogTitle>
                <DialogDescription>
                  Upload documents, images, videos, or other course materials.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Choose File</Label>
                  <Input
                    type="file"
                    onChange={handleFileUpload}
                    accept="video/*,audio/*,.mp4,.webm,.ogg,.avi,.mov,.wmv,.flv,.mkv,.m4v,.3gp,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip"
                    disabled={uploading}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Supports: Documents, Images, Videos (all formats), Audio, SCORM packages
                  </p>
                </div>
                {uploading && (
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <InteractiveElementSelector
            open={showInteractiveSelector}
            onClose={() => setShowInteractiveSelector(false)}
            onSelect={async (element) => {
              if (!moduleId) {
                toast({
                  title: "Error",
                  description: "Please save the module first before adding interactive elements",
                  variant: "destructive"
                });
                setShowInteractiveSelector(false);
                return;
              }

              try {
                // Get current content count to set the order
                const { data: existingContent, error: countError } = await supabase
                  .from('course_content')
                  .select('content_order')
                  .eq('module_id', moduleId)
                  .order('content_order', { ascending: false })
                  .limit(1);

                if (countError) throw countError;

                const nextOrder = existingContent && existingContent.length > 0 
                  ? existingContent[0].content_order + 1 
                  : 1;

                // Insert interactive element as a separate content item
                const { error: insertError } = await supabase
                  .from('course_content')
                  .insert({
                    module_id: moduleId,
                    title: element.title,
                    content_type: 'interactive',
                    content_text: JSON.stringify(element.config),
                    content_order: nextOrder,
                    metadata: {
                      interactiveType: element.type
                    }
                  });

                if (insertError) throw insertError;

                setShowInteractiveSelector(false);
                toast({
                  title: "Success",
                  description: `${element.type.charAt(0).toUpperCase() + element.type.slice(1)} element added to module`
                });

                // Refresh the page to show the new content
                window.location.reload();
              } catch (error) {
                console.error('Error adding interactive element:', error);
                toast({
                  title: "Error",
                  description: "Failed to add interactive element",
                  variant: "destructive"
                });
              }
            }}
          />

          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowInteractiveSelector(true)}
          >
            <Lightbulb className="h-4 w-4 mr-2" />
            Interactive
          </Button>
        </div>

        {/* Rich Text Editor */}
        <div className="w-full">
          <ReactQuill
            ref={setQuillRef}
            theme="snow"
            value={content}
            onChange={handleContentChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            style={{ 
              height: '300px',
              width: '100%'
            }}
          />
        </div>
        {/* Spacer to account for editor height */}
        <div style={{ height: '60px' }}></div>
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';